<script setup lang="ts">
import { computed, ref } from "vue";
import { getEnvVar } from "@/utils";
import { usePopOverPlacement, didClickAway } from "vacv-common";
import type { Product } from "vacv-common/src/types/api/promotion/Api";
import Popper from "vue3-popper";
import { VacvWasPrice as WasPrice } from "vacv-components";

interface Props {
  product: Product;
}
const props = defineProps<Props>();

const percentage = getEnvVar("VITE_SAVING_PERCENTAGE_THRESHOLD");

const wasPriceTooltipEnabled =
  getEnvVar("VITE_ENABLE_WAS_PRICE_TOOLTIP") === "true";

const { placement } = usePopOverPlacement("top");

const showWasPricePopper = ref(false);

const pricePerAdult = computed(() => props.product.price ?? NaN);

const wasPricePerAdult = computed(() => props.product.was_price ?? NaN);

const priceDifferencePercentage = computed(() => {
  if (pricePerAdult.value && wasPricePerAdult.value) {
    const difference = wasPricePerAdult.value - pricePerAdult.value;
    const percentage = (difference / wasPricePerAdult.value) * 100;
    return Math.floor(percentage);
  }
  return 0;
});

const isWasPriceSectionVisible = computed(() => {
  return priceDifferencePercentage.value >= Number(percentage);
});

const emit = defineEmits<(e: "was-price-click") => void>();

const addClickAwayListener = () => {
  window.addEventListener("mousedown", clickedAway);
  window.addEventListener("touchstart", clickedAway);
};

const removeClickAwayListener = () => {
  window.removeEventListener("mousedown", clickedAway);
  window.removeEventListener("touchstart", clickedAway);
};

const clickedAway = (event: MouseEvent | TouchEvent) => {
  if (didClickAway(event, ["popper", "was-price-popover-link"])) {
    showWasPricePopper.value = false;
    removeClickAwayListener();
  }
};

const showHideWasPrice = () => {
  if (wasPriceTooltipEnabled) {
    showWasPricePopper.value = !showWasPricePopper.value;
    if (showWasPricePopper.value) {
      emit("was-price-click");
      addClickAwayListener();
    } else {
      removeClickAwayListener();
    }
  }
};
</script>

<template>
  <div>
    <div
      v-if="isWasPriceSectionVisible"
      class="was-price-wrapper"
      :class="{ 'tooltip-none': !wasPriceTooltipEnabled }"
    >
      <popper
        arrow
        :show="showWasPricePopper"
        :placement="placement"
        @click="showHideWasPrice"
      >
        <was-price
          :percentage="priceDifferencePercentage"
          :was-price="wasPricePerAdult!"
        />
        <template #content>
          <button
            class="btn-close"
            @click.stop="showHideWasPrice"
            :aria-label="$t('common.buttons.close')"
          ></button>
          <div :show-disclaimer="true" @hide="showHideWasPrice">
            <span class="disclaimer legal">
              {{ $t("legal.disclaimer.was-price") }}
            </span>
          </div>
        </template>
      </popper>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.was-price-wrapper {
  margin-bottom: 10px !important;

  &.tooltip-none {
    :deep(.was-price a.was-price-popover-link.link) {
      cursor: default;
      pointer-events: none;

      .was {
        color: $col-black;
      }
    }
  }

  .disclaimer {
    display: block;
    margin-right: 30px;
    text-align: left;
  }
}
</style>
